import { type ReactNode, useCallback, useRef, useState } from 'react'
import './segmented-tabs.css'

export interface SegmentedTabOption<T extends string | number = string> {
  label: string
  value: T
  icon?: ReactNode
  /**
   * Tailwind classes applied to the icon wrapper for the INACTIVE state.
   * Used by the `pill` variant to colour-code icons per-option (eg. green
   * TrendingUp for income, red TrendingDown for expense). When the tab
   * becomes active, the icon is forced to `--primary-btn-text` via CSS,
   * so this only governs the inactive look.
   */
  iconClassName?: string
  disabled?: boolean
}

export type SegmentedTabsVariant = 'default' | 'nav' | 'pill'

export interface SegmentedTabsProps<T extends string | number = string> {
  options: SegmentedTabOption<T>[]
  value?: T
  defaultValue?: T
  onChange?: (value: T) => void
  variant?: SegmentedTabsVariant
  extra?: ReactNode
  className?: string
  buttonFullWidth?: boolean
  'aria-label'?: string
}

const containerClasses: Record<SegmentedTabsVariant, string> = {
  default: [
    'flex items-center justify-between',
    'bg-(--segmented-compact-bg)',
    'border border-(--segmented-compact-border)',
    'p-0.5 h-[38px]',
  ].join(' '),
  nav: [
    'flex items-center justify-between',
    'bg-(--segmented-bg)',
    'mx-[-12px] px-[30px] py-6 max-h-20',
  ].join(' '),
  // Pill: no container chrome — the tabs themselves carry the border/fill.
  // `items-center` keeps the optional `extra` aligned with the tab row.
  pill: 'flex items-center',
}

// Gap between tabs in the tablist row. The `pill` variant needs visible
// breathing room between pills; the other two variants sit edge-to-edge.
const tabListGap: Record<SegmentedTabsVariant, string> = {
  default: 'gap-0.5',
  nav: 'gap-0.5',
  pill: 'gap-2',
}

const tabBaseClasses =
  'flex items-center justify-center cursor-pointer transition-colors outline-none'

const tabVariantClasses: Record<
  SegmentedTabsVariant,
  { base: string; inactive: string; active: string; disabled: string }
> = {
  default: {
    base: 'gap-2.5 px-8 h-[34px] text-sm font-normal',
    inactive: [
      'bg-(--segmented-compact-item-bg)',
      'border border-(--segmented-compact-item-border)',
      'text-(--segmented-compact-label-color)',
    ].join(' '),
    active: [
      'bg-(--segmented-compact-active-bg)',
      'border border-transparent',
      'text-(--segmented-compact-active-text)',
    ].join(' '),
    disabled: 'opacity-50 cursor-not-allowed',
  },
  nav: {
    base: 'gap-1 px-2.5 py-[3px] h-8 min-w-[180px] text-[13px] font-medium',
    inactive: 'bg-(--tab-bg) text-(--tab-text)',
    active: 'bg-(--tab-selected-bg) text-(--tab-selected-text)',
    disabled: 'opacity-50 cursor-not-allowed',
  },
  // Pill variant — drop-in replacement for the old `.glasspro-tabs-segmented`
  // wrapper around AntD <Tabs>. 2px border, 2px radius, navy fill + white
  // text when active, navy outline on hover for inactive only. Matches the
  // iterated TxnFormModal design (see commit 7a0541e6). `font-normal` lives
  // on the inactive variant ONLY so `font-medium` on the active variant
  // wins cleanly — sharing both font-weight utilities on `base` lets
  // Tailwind's utility-order decide (which observably produced 400 for the
  // active tab; matches AntD's default of 500 for active tabs).
  pill: {
    base: 'gap-2 px-3 h-8 text-sm rounded-[2px] border-2',
    inactive:
      'bg-transparent border-(--input-border) text-[rgba(33,33,33,0.65)] font-normal hover:border-(--primary-btn-bg) hover:text-(--primary-btn-bg)',
    active: 'bg-(--primary-btn-bg) border-(--primary-btn-bg) text-(--primary-btn-text) font-medium',
    disabled: 'opacity-50 cursor-not-allowed',
  },
}

export function SegmentedTabs<T extends string | number = string>({
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  variant = 'default',
  extra,
  buttonFullWidth = false,
  className,
  'aria-label': ariaLabel = 'Tab navigation',
}: SegmentedTabsProps<T>) {
  const [internalValue, setInternalValue] = useState<T>(
    // invariant: SegmentedTabs requires options prop with at least one entry
    () => defaultValue ?? options[0]!.value,
  )
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([])

  const activeValue = controlledValue ?? internalValue

  const handleSelect = useCallback(
    (val: T) => {
      if (controlledValue === undefined) setInternalValue(val)
      onChange?.(val)
    },
    [controlledValue, onChange],
  )

  const focusTab = useCallback(
    (index: number) => {
      const enabledOptions = options.filter((o) => !o.disabled)
      const targetOption = enabledOptions[index]
      if (!targetOption) return
      const realIndex = options.indexOf(targetOption)
      tabsRef.current[realIndex]?.focus()
    },
    [options],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      const enabledOptions = options.filter((o) => !o.disabled)
      const currentEnabledIndex = enabledOptions.findIndex((o) => o.value === options[index]?.value)
      if (currentEnabledIndex === -1) return

      let nextIndex: number | null = null

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (currentEnabledIndex + 1) % enabledOptions.length
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (currentEnabledIndex - 1 + enabledOptions.length) % enabledOptions.length
          break
        case 'Home':
          nextIndex = 0
          break
        case 'End':
          nextIndex = enabledOptions.length - 1
          break
        default:
          return
      }

      e.preventDefault()
      focusTab(nextIndex)
      // invariant: nextIndex came from the switch above which only assigns
      // values guaranteed to be a valid enabledOptions index
      handleSelect(enabledOptions[nextIndex]!.value)
    },
    [options, focusTab, handleSelect],
  )

  const wrapperClass = ['segmented-tabs', containerClasses[variant], className]
    .filter(Boolean)
    .join(' ')

  const variantStyles = tabVariantClasses[variant]
  const variantModifier =
    variant === 'nav' ? 'segmented-tab--nav' : variant === 'pill' ? 'segmented-tab--pill' : ''

  const tabListClass = ['flex', tabListGap[variant], buttonFullWidth ? 'w-full' : '']
    .filter(Boolean)
    .join(' ')

  return (
    <div className={wrapperClass}>
      <div className={tabListClass} role='tablist' aria-label={ariaLabel}>
        {options.map((option, index) => {
          const isActive = activeValue === option.value
          const isDisabled = option.disabled ?? false

          const tabClass = [
            'segmented-tab',
            variantModifier,
            tabBaseClasses,
            variantStyles.base,
            isActive ? variantStyles.active : variantStyles.inactive,
            isDisabled ? variantStyles.disabled : '',
            buttonFullWidth ? 'flex-1' : '',
          ]
            .filter(Boolean)
            .join(' ')

          return (
            <button
              key={option.value}
              ref={(el) => {
                tabsRef.current[index] = el
              }}
              role='tab'
              type='button'
              aria-selected={isActive ? 'true' : 'false'}
              aria-disabled={isDisabled ? 'true' : 'false'}
              tabIndex={isActive ? 0 : -1}
              disabled={isDisabled}
              className={tabClass}
              onClick={() => !isDisabled && handleSelect(option.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
            >
              {option.icon && (
                <span
                  className={['shrink-0 flex items-center w-[18px] h-[18px]', option.iconClassName]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {option.icon}
                </span>
              )}
              <span>{option.label}</span>
            </button>
          )
        })}
      </div>
      {extra}
    </div>
  )
}
